import React, { memo, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  COLORS,
  FONT,
  settingsTabBarSx,
  settingsTabItemActiveSx,
  settingsTabItemSx,
} from "../../../shared/styles";
import { PREVIEW_PLATFORMS } from "../constants";
import InstallPreview from "./InstallPreview";
import WidgetPreview from "./WidgetPreview";
import usePreviewScale, { PREVIEW_NATURAL_HEIGHT, PREVIEW_NATURAL_WIDTH } from "./usePreviewScale";

function arePreChatFieldsEqual(prevFields, nextFields) {
  if (prevFields === nextFields) return true;
  if (!Array.isArray(prevFields) || !Array.isArray(nextFields)) return false;
  if (prevFields.length !== nextFields.length) return false;
  return prevFields.every((field, index) => {
    const next = nextFields[index];
    return (
      field?.id === next?.id
      && field?.label === next?.label
      && field?.enabled === next?.enabled
      && field?.required === next?.required
    );
  });
}

function WidgetPreviewPanel({ activeTab, settings, platform, onPlatformChange, sx }) {
  const scaleContainerRef = useRef(null);
  const scale = usePreviewScale(scaleContainerRef, PREVIEW_NATURAL_HEIGHT);
  const [agentsOnline, setAgentsOnline] = useState(true);

  const {
    primaryColor,
    position,
    greetingText,
    greetingSubtitle,
    welcomeMessage,
    logo,
    showWhenOffline,
    preChatForm,
    startChatAlways,
    allowGuest,
    preChatFields,
  } = settings;

  const showBehaviourSimulation = activeTab === "appearance" || activeTab === "behaviour";

  const previewContent = useMemo(() => {
    if (activeTab === "install") {
      return <InstallPreview settings={settings} />;
    }
    return <WidgetPreview settings={settings} agentsOnline={agentsOnline} />;
  }, [
    activeTab,
    agentsOnline,
    primaryColor,
    position,
    greetingText,
    greetingSubtitle,
    welcomeMessage,
    logo,
    showWhenOffline,
    preChatForm,
    startChatAlways,
    allowGuest,
    preChatFields,
    settings,
  ]);

  return (
    <Box
      sx={{
        width: "100%",
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        pt: "32px",
        ...sx,
      }}
    >
      <Box
        sx={{
          ...settingsTabBarSx,
          justifyContent: "space-between",
          gap: 0,
          mb: "16px",
        }}
      >
        <Box sx={{ display: "flex", gap: "24px", alignItems: "flex-end" }}>
          {PREVIEW_PLATFORMS.map((item) => (
            <Box
              key={item}
              component="button"
              type="button"
              style={{
                fontSize: "14px",
              }}
              onClick={() => onPlatformChange(item)}
              sx={{
                ...settingsTabItemSx,
                ...(platform === item ? settingsTabItemActiveSx : {}),
              }}
            >
              {item}
            </Box>
          ))}
        </Box>
        <Typography
          component="a"
          href="#"
          onClick={(e) => e.preventDefault()}
          sx={{
            color: COLORS.primary,
            fontFamily: FONT.family,
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: "18px",
            textDecoration: "none",
            pb: "16px",
            marginBottom: "-1px",
          }}
        >
          Live view
        </Typography>
      </Box>

      {showBehaviourSimulation ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            mb: "16px",
          }}
        >
          <Typography
            sx={{
              color: COLORS.textSecondary,
              fontFamily: FONT.family,
              fontSize: "12px",
              fontWeight: 400,
            }}
          >
            Simulate agents
          </Typography>
          {["Online", "Offline"].map((label) => {
            const online = label === "Online";
            const active = agentsOnline === online;
            return (
              <Box
                key={label}
                component="button"
                type="button"
                onClick={() => setAgentsOnline(online)}
                sx={{
                  height: "28px",
                  px: "10px",
                  borderRadius: "6px",
                  border: `1px solid ${active ? COLORS.primary : COLORS.border}`,
                  background: active ? "rgba(79, 70, 229, 0.08)" : COLORS.white,
                  color: active ? COLORS.primary : COLORS.textSecondary,
                  fontFamily: FONT.family,
                  fontSize: "12px",
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                {label}
              </Box>
            );
          })}
        </Box>
      ) : null}

      <Box
        ref={scaleContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: PREVIEW_NATURAL_WIDTH,
            height: PREVIEW_NATURAL_HEIGHT,
            transform: `scale(${scale})`,
            transformOrigin: "top center",
            flexShrink: 0,
          }}
        >
          {previewContent}
        </Box>
      </Box>
    </Box>
  );
}

function areSettingsEqual(prevSettings, nextSettings) {
  if (prevSettings === nextSettings) return true;
  const keys = [
    "primaryColor",
    "position",
    "greetingText",
    "greetingSubtitle",
    "welcomeMessage",
    "logo",
    "showWhenOffline",
    "preChatForm",
    "startChatAlways",
    "allowGuest",
  ];
  const scalarsEqual = keys.every((key) => prevSettings[key] === nextSettings[key]);
  return scalarsEqual && arePreChatFieldsEqual(prevSettings.preChatFields, nextSettings.preChatFields);
}

export default memo(WidgetPreviewPanel, (prev, next) =>
  prev.activeTab === next.activeTab
  && prev.platform === next.platform
  && areSettingsEqual(prev.settings, next.settings),
);
